import ErrorBoundary from 'conversations-error-reporting/error-boundaries/components/ErrorBoundary';
// @ts-expect-error module not typed
import { getCanonicalVid } from 'conversations-internal-schema/visitor/operators/visitorGetters';
import { WHATSAPP_GENERIC_CHANNEL_ID } from 'conversations-thread-data/generic-channels/public/constants';
import { getHasFileAttachment, getInboxId, getOriginalGenericChannelId, getPreview, getStatus, getThreadId, getVid, getVisitor } from 'conversations-thread-data/thread-details/public/operators';
// @ts-expect-error module not typed
import ThreadPresence from 'conversations-thread-view/presence/components/ThreadPresence';
import styled from 'foundations-theming/utils';
import { CapabilitiesEditor } from '../../../capability-editor/public/components';
import { flexColumnStyles } from '../../../editor-utilities/protected/constants';
import { ToolbarExpandButton } from '../../../pop-out/public/components';
import { ComposerSettingsMenu } from '../../../settings/protected/components';
import { useDisplayCloseLiveChatButton } from '../hooks/useDisplayCloseLiveChatButton';
import { CloseLiveChatButton } from './CloseLiveChatButton';
import { TabbedEditorErrorState } from './TabbedEditorErrorState';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { View } from 'foundations-components/components/View';
import { EditorTabs } from './EditorTabs';
import { getInboxIdFromThreadContextOrDetails } from 'composer-data/thread-context/public/utils';
import { ReplyEditorLoadingState } from '../../../reply-editor/protected/loading';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PositioningWrapper = styled.div.withConfig({
  displayName: "TabbedEditor__PositioningWrapper",
  componentId: "sc-1w8qibf-0"
})(["position:relative;flex-grow:1;flex-shrink:1;", " min-inline-size:100px;"], flexColumnStyles);
const TabButtonsGroup = styled.div.withConfig({
  displayName: "TabbedEditor__TabButtonsGroup",
  componentId: "sc-1w8qibf-1"
})(["flex-shrink:1;display:flex;align-items:center;"]);
const EmptyDisabledMessage = () => {
  return /*#__PURE__*/_jsx(Text, {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "composer.empty-disabled-message"
    })
  });
};
EmptyDisabledMessage.displayName = 'EmptyDisabledMessage';
function getComponentDataForLogging(threadDetails) {
  return threadDetails ? {
    threadId: getThreadId(threadDetails),
    genericChannelId: getOriginalGenericChannelId(threadDetails),
    status: getStatus(threadDetails),
    inboxId: getInboxId(threadDetails),
    preview: getPreview(threadDetails),
    vid: getVid(threadDetails),
    hasFileAttachment: getHasFileAttachment(threadDetails)
  } : {};
}
export function TabbedEditor({
  associatedObjectId,
  associatedObjectTypeId,
  asyncEmptyReply,
  callingSource,
  canSubmit = true,
  capabilitiesBlob,
  currentChannelId,
  disableCalling,
  disabled,
  disabledMessage,
  editorComponent,
  editorFragment,
  expandable = false,
  isKnownVisitor,
  isLoading,
  latestCMFGenericChannelId,
  onCloseShepherd,
  onDraftChange,
  onSelectedChange,
  originalGenericChannelId,
  renderExistingFileSelect,
  renderSubmitButton,
  replyRecommendationsEnabled,
  sendDisabledValidator,
  submitHandler,
  threadDetails,
  hideComments,
  CustomHeaderActions,
  defaultSendFromEnabled,
  customEditorViews,
  macrosMenuConfig,
  handleFirstClick,
  threadContext
}) {
  var _customEditorViews$vi;
  const displayCloseLiveChatButton = useDisplayCloseLiveChatButton({
    currentChannelId
  });
  if (disabled) {
    if (!disabledMessage) {
      Raven.captureException(new Error('TabbedEditor is disabled but no disabledMessage was provided.'));
      return /*#__PURE__*/_jsx(EmptyDisabledMessage, {});
    }
    return /*#__PURE__*/_jsx(View, {
      padding: "400",
      textAlign: "center",
      children: disabledMessage
    });
  }
  const visitor = getVisitor(threadDetails);
  const subjectId = getCanonicalVid(visitor);
  const inboxId = getInboxIdFromThreadContextOrDetails(threadContext, threadDetails);
  const threadId = getThreadId(threadDetails);
  const eligibleChannel = currentChannelId !== WHATSAPP_GENERIC_CHANNEL_ID;
  const displayComposerSettings = eligibleChannel && replyRecommendationsEnabled;
  const CustomEditorComponent = customEditorViews === null || customEditorViews === void 0 || (_customEditorViews$vi = customEditorViews.views.find(view => view.key === currentChannelId)) === null || _customEditorViews$vi === void 0 ? void 0 : _customEditorViews$vi.render;
  const TabbedComponent = CustomEditorComponent || editorComponent || CapabilitiesEditor;
  const shouldRenderTabbedComponent = CustomEditorComponent ? true : !isLoading && capabilitiesBlob;
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    renderError: TabbedEditorErrorState,
    errorLoggingTitle: "An uncaught TabbedChannels error occurred",
    errorBoundaryName: "TabbedChannelsBoundary",
    onGetComponentDataForLogging: () => getComponentDataForLogging(threadDetails),
    children: /*#__PURE__*/_jsxs(PositioningWrapper, {
      "data-test-id": "TabbedEditorPositionWrapper",
      children: [isLoading || !originalGenericChannelId ? /*#__PURE__*/_jsx(ReplyEditorLoadingState, {}) : /*#__PURE__*/_jsxs(Flex, {
        backgroundColor: "fill-surface-default",
        borderBlockStartWidth: "100",
        borderBlockStartColor: "border-core-subtle",
        borderBlockStartStyle: "solid",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsx(EditorTabs, {
          editorFragment: editorFragment,
          onSelectedChange: onSelectedChange,
          associatedObjectId: associatedObjectId,
          associatedObjectTypeId: associatedObjectTypeId,
          callingSource: callingSource,
          disableCalling: disableCalling,
          isKnownVisitor: isKnownVisitor,
          currentChannelId: currentChannelId,
          originalGenericChannelId: originalGenericChannelId,
          subjectId: subjectId,
          inboxId: inboxId,
          threadId: `${threadId}`,
          hideComments: hideComments,
          customEditorViews: customEditorViews,
          handleFirstClick: handleFirstClick
        }), /*#__PURE__*/_jsxs(TabButtonsGroup, {
          children: [CustomHeaderActions ? /*#__PURE__*/_jsx(Flex, {
            display: "inline-flex",
            onClick: () => handleFirstClick === null || handleFirstClick === void 0 ? void 0 : handleFirstClick('CustomHeaderActions'),
            children: CustomHeaderActions
          }) : null, /*#__PURE__*/_jsx(ThreadPresence, {
            threadId: threadId
          }), displayComposerSettings && /*#__PURE__*/_jsx(ComposerSettingsMenu, {
            handleFirstClick: handleFirstClick
          }), displayCloseLiveChatButton ? /*#__PURE__*/_jsx(CloseLiveChatButton, {
            onSelectedChange: onSelectedChange,
            handleFirstClick: handleFirstClick
          }) : expandable ? /*#__PURE__*/_jsx(ToolbarExpandButton, {
            editorKey: "replyEditor",
            handleFirstClick: handleFirstClick
          }) : null]
        })]
      }), /*#__PURE__*/_jsx(Flex, {
        flexGrow: 1,
        flexDirection: "column",
        alignItems: "stretch",
        backgroundColor: "fill-surface-default",
        borderBlockStartWidth: "100",
        borderBlockStartColor: "border-core-subtle",
        borderBlockStartStyle: "solid",
        children: shouldRenderTabbedComponent && /*#__PURE__*/_jsx(TabbedComponent, {
          asyncEmptyReply: asyncEmptyReply,
          canSubmit: canSubmit,
          capabilitiesBlob: capabilitiesBlob,
          currentChannelId: currentChannelId,
          latestCMFGenericChannelId: latestCMFGenericChannelId,
          onCloseShepherd: onCloseShepherd,
          onDraftChange: onDraftChange,
          originalGenericChannelId: originalGenericChannelId || null,
          renderExistingFileSelect: renderExistingFileSelect,
          replyRecommendationsEnabled: replyRecommendationsEnabled,
          renderSubmitButton: renderSubmitButton,
          sendDisabledValidator: sendDisabledValidator,
          submitHandler: submitHandler,
          threadDetails: threadDetails,
          defaultSendFromEnabled: defaultSendFromEnabled,
          macrosMenuConfig: macrosMenuConfig,
          handleFirstClick: handleFirstClick,
          threadContext: threadContext
        })
      })]
    })
  });
}
TabbedEditor.displayName = '';